import * as SimpleSwitch from "a-simple-switch";
import "./uiSwitch.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';

const UI_SWITCH_SELECTOR = '[data-ui-switch]';

type UISwitchOptions = {
  onChange?: (isChecked: boolean) => void;
};

export class UISwitch {
  private readonly id: string;
  private static registry = new ComponentRegistry<UISwitch>('ui-switch');
  private readonly input: HTMLInputElement;
  private simpleSwitch: SimpleSwitch.Switch | null = null;
  private readonly onChange?: (isChecked: boolean) => void;

  private constructor(
    input: HTMLInputElement,
    options: UISwitchOptions = {}
  ) {
    if (!(input instanceof HTMLInputElement)) {
      throw new Error('UISwitch: Element must be an HTMLInputElement');
    }

    this.input = input;
    this.id = UISwitch.registry.register(input, this);
    this.onChange = options.onChange;
    this.init();
  }

  private init(): void {
    this.simpleSwitch = new SimpleSwitch.Switch({
      element: this.input
    });

    // Set button type to prevent form submission
    const btn = this.input.nextElementSibling as HTMLButtonElement;
    if (btn) {
      btn.type = 'button';
    }

    // Bind change handler if provided
    if (this.onChange) {
      this.input.addEventListener('change', this.handleChange);
    }
  }

  private handleChange = (): void => {
    if (this.onChange) {
      this.onChange(this.input.checked);
    }
  };

  public isChecked(): boolean {
    return this.input.checked;
  }

  public setChecked(value: boolean): void {
    this.input.checked = value;
    // Trigger SimpleSwitch to update its UI
    this.input.dispatchEvent(new Event('change'));
  }

  public static create(
    target: string | HTMLInputElement | NodeListOf<HTMLElement> | HTMLElement[] = UI_SWITCH_SELECTOR,
    options: UISwitchOptions = {}
  ): UISwitch[] | null {
    const inputs = ComponentRegistry.processTargets<UISwitch, HTMLElement>(
        UISwitch.registry,
        target,
        [HTMLInputElement]
    ) as HTMLInputElement[];
    if (!inputs.length) return null;
    return inputs.map(input => new UISwitch(input, options));
  }

    public static getSwitch(id: string): UISwitch | null {
        return UISwitch.registry.get(id) ?? null;
    }

  public destroy(): void {
    if (this.onChange) {
      this.input.removeEventListener('change', this.handleChange);
    }

    // SimpleSwitch doesn't expose a destroy method, so we need to clean up manually
    const btn = this.input.nextElementSibling;
    if (btn) {
      btn.remove();
    }

    this.simpleSwitch = null;

    UISwitch.registry.unregister(this.id);
  }
}

// Legacy function for backward compatibility
export const setSwitches = (): void => {
  SimpleSwitch.init();
};